import React from 'react'
import { useHistory } from 'react-router-dom'
import { identifyMaybeAndBroadcast } from 'web/src/shared/Auth'
import { useAccountQuery, useTrackMutation } from '@mote/api'
import { InfoCircleIcon, TickOutlineIcon } from '@mote/assets/icons'
import { LEGALS_VERSION, TRACK_EVENT_NAMES } from '@mote/helpers/constants'
import { useGoalsState } from '@mote/helpers/hooks/business'
import { snakify } from '@mote/helpers/misc'
import { useAuth } from '../../auth/useAuth'
import { getGodModeOptions } from '../../shared/Utils'
import { MessageCard } from './components'
import { useOnboarding } from './context'

const MAX_POLLS = 20
const POLL_SLEEP = 1000

export const OnboardingInvitation = () => {
  const history = useHistory()
  const { user } = useAuth()
  const { updateProperties } = useOnboarding()
  const {
    mutate: track,
    loading: isTrackMutating,
    error: trackError
  } = useTrackMutation()
  const { onboardingComplete } = useGoalsState()
  const {
    mutate: fetchAccount,
    loading: isAccountLoading,
    error: accountError
  } = useAccountQuery()
  const [account, setAccount] = React.useState(null)
  const [genericError, setError] = React.useState(false)
  const [isStateLoading, setIsStateLoading] = React.useState(false)
  const isInstitutionalMinor = user?.isInstitutional && user?.isMinor

  const error = React.useMemo(
    () => genericError || trackError || accountError,
    [genericError, trackError, accountError]
  )

  const handleProceed = React.useCallback(
    async (polls = 0) => {
      if (isInstitutionalMinor) {
        if (!isStateLoading) setIsStateLoading(true)

        if (!onboardingComplete) {
          const response = await fetchAccount({
            variables: getGodModeOptions() || {}
          })

          if (response.error) {
            if (response?.error) console.log(response.error)
            setIsStateLoading(false)
            return
          }

          const { goals } = response.data ?? {}

          if (goals?.userOnboarding !== 'completed') {
            if (polls < MAX_POLLS) {
              return setTimeout(() => handleProceed(polls + 1), POLL_SLEEP)
            } else {
              setError(true)
              setIsStateLoading(false)
              return
            }
          }

          identifyMaybeAndBroadcast()
        }

        const response = await updateProperties({
          payload: {
            legals: LEGALS_VERSION
          }
        })

        if (response.error) {
          setError(true)
          setIsStateLoading(false)
          return
        }

        history.push('/')
      } else {
        await track({
          variables: {
            event: TRACK_EVENT_NAMES.commerce.insitutionalLicenseSeen
          }
        })

        history.push('/onboarding')
      }
    },
    [isInstitutionalMinor, isStateLoading, onboardingComplete]
  )

  const { icon, title, message, ctaProps } = React.useMemo(() => {
    if (!account || !user) return {}
    const isInstitutionalFreePlan = user.isInstitutional && !user.isPaid

    let text
    if (onboardingComplete || isInstitutionalMinor) {
      text = 'Continue to dashboard'
    } else if (isInstitutionalFreePlan) {
      text = 'Continue to account'
    } else {
      text = 'Set up my account'
    }

    let message = `Lucky you! ${account.accountOwnerName} from ${account.institutionName} has given you a Mote Unlimited license.`

    if (isInstitutionalMinor) {
      message = `Your account is linked to ${account.institutionName}. Hit the button below to go to the dashboard and get started.`
    } else if (isInstitutionalFreePlan) {
      message = `Your account is linked to ${account.institutionName}. You are currently on a free plan. To upgrade, speak to your account admin.`
    } else if (onboardingComplete) {
      message = `${message} You’ll see these changes reflected in your account from now on.`
    } else {
      message = `${message} Hit the button below to set up your account and get started.`
    }

    let icon = '🎉'

    if (isInstitutionalMinor) {
      icon = <InfoCircleIcon width={36} height={36} />
    } else if (isInstitutionalFreePlan) {
      icon = <TickOutlineIcon width={58} height={58} />
    }

    return {
      title:
        isInstitutionalFreePlan || isInstitutionalMinor
          ? 'You’re linked to an organisation'
          : 'You’ve been given an Unlimited license!',
      message,
      icon,
      ctaProps: {
        text,
        loadingText: 'Proceeding',
        loading: isTrackMutating || isStateLoading || isAccountLoading,
        onClick: handleProceed
      }
    }
  }, [
    isInstitutionalMinor,
    isTrackMutating,
    user,
    onboardingComplete,
    account,
    isAccountLoading,
    isStateLoading
  ])

  React.useEffect(() => {
    async function queryAccount() {
      const response = await fetchAccount({
        variables: getGodModeOptions() || {}
      })
      if (!response || response.error || !response.data) return

      setAccount(response.data.account)
    }
    queryAccount()
  }, [])

  React.useEffect(() => {
    if (!isInstitutionalMinor) return

    track({
      variables: snakify({
        event: TRACK_EVENT_NAMES.goals.completed,
        goalName: 'userOnboarding'
      })
    }).catch(console.error)
  }, [])

  if (error) {
    return MessageCard({
      title: 'Oh dear...',
      message:
        'An unexpected error happened. Please try again and if the problem persists, kindly contact support.',
      icon: '❌',
      ctaProps: {
        text: 'Try again',
        onClick: () => window.location.reload()
      }
    })
  }

  return (
    <MessageCard
      loading={!isStateLoading && isAccountLoading}
      title={title}
      message={message}
      icon={icon}
      ctaProps={ctaProps}
    />
  )
}
